import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Search,
  SlidersHorizontal,
  ArrowUpDown,
  User,
  MapPin,
  Building2,
  Users,
  Video,
  Phone,
  Mail,
  Camera,
  Upload,
  FileText,
  Shield,
  BadgeCheck,
  Menu,
  Plus,
  Trash2,
  Eye,
  EyeOff,
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const initialFormState = {
  title: '',
  firstName: '',
  familyName: '',
  otherName: '',
  gender: '',
  birthDate: '',
  medicalRecordNumber: '',
  patientAccount: '',
  patientGroups: '',
  patientAccountStatus: 'Active',
  foreignNational: false,
  privacyGroups: '',
  nationalId: '',
  passport: '',
  driversLicence: '',
  legacyUniqueIdentifier: '',
  identificationDocuments: [],
  maritalStatus: '',
  employmentStatus: '',
  educationLevel: '',
  cardPrints: false,
  wristBandPrints: false,
  labelPrints: false,
  countryCode: 'Zimbabwe',
  mobilePhoneNumber: '',
  primaryEmail: '',
  country: '',
  city: '',
  addressLine: '',
  residence: '',
  postalCode: '',
  language: '',
  languagesSpoken: '',
  preferredLanguage: 'English',
  employer: '',
  kinTitle: '',
  kinFirstName: '',
  kinFamilyName: '',
  kinRelation: '',
  kinGender: '',
  kinBirthDate: '',
  kinCountryCode: 'Zimbabwe',
  kinMobilePhoneNumber: '',
  telemedicineEnabled: false,
  profileImage: '',
  password: '',
};

const tabs = [
  { id: 'personal', label: 'Personal details', icon: User },
  { id: 'contacts', label: 'Contacts & addresses', icon: MapPin },
  { id: 'employer', label: 'Employer', icon: Building2 },
  { id: 'kin', label: 'Next of Kin', icon: Users },
  { id: 'telemedicine', label: 'Telemedicine', icon: Video },
];

const sectionTitleClass = 'text-sm font-semibold text-slate-700';
const labelClass = 'block text-[13px] font-semibold text-slate-700 mb-2';
const inputClass =
  'w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 outline-none transition focus:border-violet-500 focus:ring-4 focus:ring-violet-100';
const selectClass =
  'w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 outline-none transition focus:border-violet-500 focus:ring-4 focus:ring-violet-100';
const textareaClass =
  'w-full min-h-[100px] px-3 py-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 outline-none transition focus:border-violet-500 focus:ring-4 focus:ring-violet-100';
const checkboxClass = 'h-4 w-4 accent-violet-600';
const cardClass = ' border border-slate-200 bg-white shadow-sm';

const Patients = () => {
  const [patients, setPatients] = useState([]);
  const [activeTab, setActiveTab] = useState('personal');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [mode, setMode] = useState('create');
  const [form, setForm] = useState(initialFormState);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('familyName');
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${window.location.protocol}//${window.location.hostname}/api/patients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPatients(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error(error);
    }
  };


  const handleDeletePatient = async () => {
    if (!selectedPatient?.id) {
      toast.error('No patient selected');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedPatient.firstName || ''} ${selectedPatient.lastName || ''}?`)) return;

    const toastId = toast.loading('Deleting patient...');
    const token = localStorage.getItem('token');

    try {
      await axios.delete(`http://localhost:5000/api/patients/${selectedPatient.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success('Patient deleted successfully', { id: toastId });

      setSelectedPatient(null);
      setForm(initialFormState);
      setActiveTab('personal');
      setMode('create');

      await fetchPatients();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Error deleting patient', { id: toastId });
    }
  };


  const calculateAge = (birthDate) => {
    if (!birthDate) return '';
    const dob = new Date(birthDate);
    if (Number.isNaN(dob.getTime())) return '';
    const diff = Date.now() - dob.getTime();
    const ageDate = new Date(diff);
    return Math.abs(ageDate.getUTCFullYear() - 1970);
  };

  const formatHeaderMeta = () => {
    const age = calculateAge(form.birthDate);
    const gender = form.gender || '-';
    const dob = form.birthDate || '-';
    const mrn = form.medicalRecordNumber || 'N/A';
    return `${dob}${age ? ` (${age} yr)` : ''}, ${gender} • MRN-${mrn}`;
  };

  const selectPatient = (patient) => {
    setSelectedPatient(patient);
    setMode('edit');
    setForm({
      title: patient.title || '',
      firstName: patient.firstName || '',
      familyName: patient.lastName || '',
      otherName: patient.otherName || '',
      gender: patient.gender || '',
      birthDate: patient.dateOfBirth || '',
      medicalRecordNumber: patient.medicalRecordNumber || '',
      patientAccount: patient.patientAccount || '',
      patientGroups: patient.patientGroups || '',
      patientAccountStatus: patient.patientAccountStatus || 'Active',
      foreignNational: patient.foreignNational || false,
      privacyGroups: patient.privacyGroups || '',
      nationalId: patient.nationalId || '',
      passport: patient.passport || '',
      driversLicence: patient.driversLicence || '',
      legacyUniqueIdentifier: patient.legacyUniqueIdentifier || '',
      identificationDocuments: patient.identificationDocuments || [],
      maritalStatus: patient.maritalStatus || '',
      employmentStatus: patient.employmentStatus || '',
      educationLevel: patient.educationLevel || '',
      cardPrints: patient.cardPrints || false,
      wristBandPrints: patient.wristBandPrints || false,
      labelPrints: patient.labelPrints || false,
      countryCode: patient.countryCode || 'Zimbabwe',
      mobilePhoneNumber: patient.phone || '',
      primaryEmail: patient.email || '',
      country: patient.country || '',
      city: patient.city || '',
      addressLine: patient.addressLine || '',
      residence: patient.residence || '',
      postalCode: patient.postalCode || '',
      language: patient.language || '',
      languagesSpoken: patient.languagesSpoken || '',
      preferredLanguage: patient.preferredLanguage || 'English',
      employer: patient.employer || '',
      kinTitle: patient.kinTitle || '',
      kinFirstName: patient.kinFirstName || '',
      kinFamilyName: patient.kinFamilyName || '',
      kinRelation: patient.kinRelation || '',
      kinGender: patient.kinGender || '',
      kinBirthDate: patient.kinBirthDate || '',
      kinCountryCode: patient.kinCountryCode || 'Zimbabwe',
      kinMobilePhoneNumber: patient.kinMobilePhoneNumber || '',
      telemedicineEnabled: patient.telemedicineEnabled || false,
      profileImage: patient.profileImage || '',
      password: '', // We don't load the hashed password
    });
  };

  const buildPatientPayload = (forceActive = false) => {
    const status = forceActive ? 'active' : 'draft';

    return {
      title: form.title,
      firstName: form.firstName,
      lastName: form.familyName,
      otherName: form.otherName,
      dateOfBirth: form.birthDate,
      gender: form.gender,
      medicalRecordNumber: form.medicalRecordNumber,
      patientAccount: form.patientAccount,
      patientGroups: form.patientGroups,
      patientAccountStatus: form.patientAccountStatus,
      foreignNational: form.foreignNational,
      privacyGroups: form.privacyGroups,
      nationalId: form.nationalId,
      passport: form.passport,
      driversLicence: form.driversLicence,
      legacyUniqueIdentifier: form.legacyUniqueIdentifier,
      maritalStatus: form.maritalStatus,
      employmentStatus: form.employmentStatus,
      educationLevel: form.educationLevel,
      cardPrints: form.cardPrints,
      wristBandPrints: form.wristBandPrints,
      labelPrints: form.labelPrints,
      phone: form.mobilePhoneNumber,
      email: form.primaryEmail,
      countryCode: form.countryCode,
      country: form.country,
      city: form.city,
      addressLine: form.addressLine,
      residence: form.residence,
      postalCode: form.postalCode,
      address: `${form.addressLine || ''}, ${form.residence || ''}, ${form.city || ''}, ${form.country || ''}`.replace(/^,\s*|,\s*$/g, ''),
      language: form.language,
      languagesSpoken: form.languagesSpoken,
      preferredLanguage: form.preferredLanguage,
      employer: form.employer,
      kinTitle: form.kinTitle,
      kinFirstName: form.kinFirstName,
      kinFamilyName: form.kinFamilyName,
      kinRelation: form.kinRelation,
      kinGender: form.kinGender,
      kinBirthDate: form.kinBirthDate && form.kinBirthDate !== 'Invalid date'
        ? form.kinBirthDate
        : null,
      kinCountryCode: form.kinCountryCode,
      kinMobilePhoneNumber: form.kinMobilePhoneNumber,
      telemedicineEnabled: form.telemedicineEnabled,
      profileImage: form.profileImage || null,
      status,
      isActive: true,
      password: form.password,
    };
  };

  const compressImage = (file, maxWidth = 600, quality = 0.7) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const scale = Math.min(1, maxWidth / img.width);
          canvas.width = img.width * scale;
          canvas.height = img.height * scale;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedBase64);
        };
        img.onerror = reject;
        img.src = event.target.result;
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleSubmit = async (e, forceActive = false) => {
    if (e) e.preventDefault();

    const toastId = toast.loading(forceActive ? 'Saving patient...' : 'Saving draft...');
    const token = localStorage.getItem('token');

    try {
      const patientData = buildPatientPayload(forceActive);

      if (selectedPatient?.id) {
        await axios.put(`http://localhost:5000/api/patients/${selectedPatient.id}`, patientData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success(forceActive ? 'Patient updated successfully' : 'Patient draft updated successfully', { id: toastId });
      } else {
        await axios.post(`${window.location.protocol}//${window.location.hostname}/api/patients`, patientData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success(forceActive ? 'Patient created successfully' : 'Patient draft saved successfully', { id: toastId });
      }

      await fetchPatients();

      if (!selectedPatient?.id) {
        setForm(initialFormState);
        setMode('create');
      }
    } catch (error) {
      console.error(error);
      toast.error('Error saving patient', { id: toastId });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleProfileImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const compressed = await compressImage(file, 600, 0.7);
      setForm((prev) => ({
        ...prev,
        profileImage: compressed,
      }));
    } catch (error) {
      console.error('Image compression failed:', error);
    }
  };

  const handleFileChange = (e) => {
    setForm((prev) => ({
      ...prev,
      identificationDocuments: Array.from(e.target.files || []),
    }));
  };

  const filteredPatients = useMemo(() => {
    if (!searchTerm.trim()) return patients;

    const term = searchTerm.toLowerCase();
    return patients.filter((patient) => {
      const fullName = `${patient.firstName || ''} ${patient.lastName || ''}`.toLowerCase();
      return (
        fullName.includes(term) ||
        (patient.email || '').toLowerCase().includes(term) ||
        (patient.phone || '').includes(searchTerm) ||
        (patient.medicalRecordNumber || '').toLowerCase().includes(term)
      );
    });
  }, [patients, searchTerm]);

  const sortedPatients = useMemo(() => {
    const field = sortBy === 'firstName' ? 'firstName' : 'lastName';
    return [...filteredPatients].sort((a, b) => {
      const va = (a[field] || '').toLowerCase();
      const vb = (b[field] || '').toLowerCase();
      return va.localeCompare(vb);
    });
  }, [filteredPatients, sortBy]);

  const getVisitBadge = (patient) => {
    if (patient.status === 'draft') {
      return {
        label: 'Draft',
        classes: 'bg-amber-100 text-amber-700 border border-amber-200',
      };
    }

    return {
      label: 'Active',
      classes: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
    };
  };

  const displayName =
    selectedPatient || mode === 'edit'
      ? `${form.firstName || 'New'} ${form.familyName || 'Patient'}`
      : 'Create new patient';

  return (
    <div className="bg-slate-100 text-slate-800">
      <section className="flex max-w-[1500px] mx-auto h-screen overflow-hidden">
        <aside className="w-[280px] overflow-y-auto h-screen shrink-0 bg-white border-r border-slate-200 flex flex-col sticky top-0">
          <div className="p-4 border-b border-slate-200 bg-white shrink-0">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search patients"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full h-12 pl-12 pr-12 rounded-xl border border-slate-200 bg-slate-50 text-sm outline-none focus:ring-4 focus:ring-violet-100 focus:border-violet-500"
              />
              <SlidersHorizontal className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            </div>

            <div className="mt-3 flex gap-2">
              <select className="flex-1 h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-600">
                <option>Search in</option>
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-[140px] h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-600"
              >
                <option value="familyName">Family name</option>
                <option value="firstName">First name</option>
              </select>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto">
            {sortedPatients.length === 0 ? (
              <div className="p-8 text-sm text-slate-500">No patients found</div>
            ) : (
              sortedPatients.map((patient) => {
                const badge = getVisitBadge(patient);
                const image = patient.profileImage || '';

                return (
                  <button
                    key={patient.id}
                    type="button"
                    onClick={() => selectPatient(patient)}
                    className={`w-full text-left px-4 py-4 border-b border-slate-100 transition ${selectedPatient?.id === patient.id ? 'bg-violet-50' : 'hover:bg-slate-50'
                      }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-2xl overflow-hidden bg-slate-200 flex items-center justify-center shrink-0">
                        {image ? (
                          <img src={image} alt="Patient" className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-6 h-6 text-slate-500" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-900 break-words">
                              {patient.firstName} {patient.lastName}
                            </h3>
                            <p className="mt-1 text-xs text-slate-500">
                              MRN: {patient.medicalRecordNumber || 'N/A'}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                              Phone: {patient.phone || 'N/A'}
                            </p>
                          </div>

                          <span className="text-xs font-medium text-slate-400">
                            {patient.status === 'draft' ? 'Draft' : 'Active'}
                          </span>
                        </div>

                        <div className="mt-3">
                          <span
                            className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold ${badge.classes}`}
                          >
                            {badge.label}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        <section className="flex-1 h-screen overflow-y-auto relative">
          {selectedPatient || mode === 'edit' ? (
            <div className="p-0">
              <div className="max-w-[1500px] mx-auto space-y-4">
                <div className={`${cardClass} overflow-hidden`}>
                  <div className="px-6 py-6 bg-gradient-to-r from-slate-50 to-violet-50">
                    <div className="flex items-start justify-between gap-8">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h1 className="text-2xl font-bold uppercase tracking-wide text-slate-800">
                            {displayName}
                          </h1>
                          <span
                            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${(selectedPatient?.status || 'draft') === 'active'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-amber-100 text-amber-700'
                              }`}
                          >
                            {(selectedPatient?.status || 'draft') === 'active' ? 'Active' : 'Draft'}
                          </span>
                        </div>

                        <p className="text-sm font-medium text-slate-500">{formatHeaderMeta()}</p>

                        <div className="mt-8 grid grid-cols-5 gap-5">
                          <SummaryItem label="National ID" value={form.nationalId || '-'} />
                          <SummaryItem label="Mobile phone" value={form.mobilePhoneNumber || '-'} />
                          <SummaryItem label="City" value={form.city || '-'} />
                          <SummaryItem label="Address line" value={form.addressLine || '-'} />
                          <SummaryItem label="Residence" value={form.residence || '-'} />
                        </div>
                      </div>

                      <div className="w-[210px] shrink-0 flex flex-col items-center">
                        <div className="relative w-28 h-28 rounded-3xl overflow-hidden ring-4 ring-white shadow-lg bg-slate-200">
                          {form.profileImage ? (
                            <img src={form.profileImage} alt="Patient" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <User className="w-12 h-12 text-slate-500" />
                            </div>
                          )}

                          <label className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-violet-600 text-white flex items-center justify-center cursor-pointer shadow">
                            <Camera className="w-4 h-4" />
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleProfileImageChange}
                              className="hidden"
                            />
                          </label>
                        </div>

                        <label className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-violet-700 cursor-pointer">
                          <Upload className="w-4 h-4" />
                          Upload profile image
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleProfileImageChange}
                            className="hidden"
                          />
                        </label>

                        {selectedPatient?.id && (
                          <button
                            type="button"
                            onClick={handleDeletePatient}
                            className="mt-3 inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-100 transition"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete patient
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="px-4 pt-5 bg-white">
                    <div className="grid grid-cols-5 gap-2">
                      {tabs.map((tab) => {
                        const Icon = tab.icon;
                        const active = activeTab === tab.id;

                        return (
                          <button
                            key={tab.id}
                            type="button"
                            onClick={() => setActiveTab(tab.id)}
                            className="flex flex-col items-center gap-2 pb-4"
                          >
                            <div
                              className={`w-10 h-10 rounded-2xl flex items-center justify-center transition ${active ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-500'
                                }`}
                            >
                              <Icon className="w-5 h-5" />
                            </div>
                            <span className={`text-sm font-semibold ${active ? 'text-violet-700' : 'text-slate-500'}`}>
                              {tab.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    <div className="grid grid-cols-5">
                      {tabs.map((tab) => (
                        <div key={tab.id} className="relative h-3 bg-slate-200">
                          <div
                            className={`absolute top-0 left-0 h-3 rounded-full transition-all ${activeTab === tab.id ? 'w-full bg-violet-500' : 'w-0'
                              }`}
                          />
                          <div
                            className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-5 w-5 rounded-full border-4 transition ${activeTab === tab.id
                              ? 'bg-white border-violet-500'
                              : 'bg-slate-200 border-slate-200'
                              }`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <form onSubmit={(e) => handleSubmit(e, true)} className="px-6 py-8 bg-slate-50">
                    {activeTab === 'personal' && (
                      <div className="space-y-8">
                        <SectionRow title="Demographic details">
                          <div className="grid grid-cols-3 gap-4">
                            <Field label="Title">
                              <select name="title" value={form.title} onChange={handleChange} className={selectClass}>
                                <option value="">Select</option>
                                <option value="Mr">Mr.</option>
                                <option value="Mrs">Mrs.</option>
                                <option value="Ms">Ms.</option>
                                <option value="Dr">Dr.</option>
                              </select>
                            </Field>

                            <Field label="First name (required)">
                              <input name="firstName" value={form.firstName} onChange={handleChange} className={inputClass} />
                            </Field>

                            <Field label="Family name (required)">
                              <input name="familyName" value={form.familyName} onChange={handleChange} className={inputClass} />
                            </Field>

                            <Field label="Other name">
                              <input name="otherName" value={form.otherName} onChange={handleChange} className={inputClass} />
                            </Field>
                          </div>

                          <div className="grid grid-cols-3 gap-4 mt-4">
                            <Field label="Gender (required)">
                              <select name="gender" value={form.gender} onChange={handleChange} className={selectClass}>
                                <option value="">Select</option>
                                <option value="female">Female</option>
                                <option value="male">Male</option>
                                <option value="other">Other</option>
                              </select>
                            </Field>

                            <Field label="Birth date (required)">
                              <input type="date" name="birthDate" value={form.birthDate} onChange={handleChange} className={inputClass} />
                            </Field>

                            <Field label="Medical record number">
                              <input
                                name="medicalRecordNumber"
                                value={form.medicalRecordNumber}
                                onChange={handleChange}
                                className={inputClass}
                              />
                            </Field>

                            <Field label="Login Password">
                              <div className="relative">
                                <input
                                  type={showPwd ? 'text' : 'password'}
                                  name="password"
                                  value={form.password}
                                  onChange={handleChange}
                                  className={`${inputClass} pr-12`}
                                  placeholder="Set login password"
                                />
                                <button
                                  type="button"
                                  onClick={() => setShowPwd(!showPwd)}
                                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 hover:bg-slate-100 rounded-xl transition-all text-slate-400"
                                >
                                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                              </div>
                            </Field>
                          </div>
                        </SectionRow>

                        <SectionRow title="Patient account">
                          <div className="grid grid-cols-3 gap-4">
                            <Field label="Patient groups">
                              <input name="patientGroups" value={form.patientGroups} onChange={handleChange} className={inputClass} />
                            </Field>

                            <Field label="Patient account status">
                              <select
                                name="patientAccountStatus"
                                value={form.patientAccountStatus}
                                onChange={handleChange}
                                className={selectClass}
                              >
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                              </select>
                            </Field>

                            <Field label="Foreign national">
                              <div className="h-11 flex items-center px-1">
                                <input
                                  type="checkbox"
                                  name="foreignNational"
                                  checked={form.foreignNational}
                                  onChange={handleChange}
                                  className={checkboxClass}
                                />
                              </div>
                            </Field>
                          </div>

                          <div className="grid grid-cols-3 gap-4 mt-4">
                            <Field label="Privacy groups">
                              <input name="privacyGroups" value={form.privacyGroups} onChange={handleChange} className={inputClass} />
                            </Field>
                          </div>
                        </SectionRow>

                        <SectionRow title="Identification details">
                          <div className="grid grid-cols-3 gap-4">
                            <Field label="National ID">
                              <input name="nationalId" value={form.nationalId} onChange={handleChange} className={inputClass} />
                            </Field>

                            <Field label="Passport">
                              <input name="passport" value={form.passport} onChange={handleChange} className={inputClass} />
                            </Field>

                            <Field label="Drivers licence">
                              <input name="driversLicence" value={form.driversLicence} onChange={handleChange} className={inputClass} />
                            </Field>
                          </div>

                          <div className="grid grid-cols-3 gap-4 mt-4">
                            <Field label="Legacy unique identifier">
                              <input
                                name="legacyUniqueIdentifier"
                                value={form.legacyUniqueIdentifier}
                                onChange={handleChange}
                                className={inputClass}
                              />
                            </Field>
                          </div>
                        </SectionRow>

                        <SectionRow title="Identification documents">
                          <div className="border border-dashed border-slate-300 rounded-2xl bg-white min-h-[130px] flex items-center justify-center px-6">
                            <div className="text-center">
                              <div className="w-12 h-12 rounded-2xl bg-violet-100 text-violet-700 mx-auto flex items-center justify-center mb-3">
                                <FileText className="w-5 h-5" />
                              </div>
                              <label className="text-violet-700 font-semibold cursor-pointer">
                                Upload files
                                <input
                                  type="file"
                                  multiple
                                  onChange={handleFileChange}
                                  accept="image/*,.pdf,.doc,.docx"
                                  className="hidden"
                                />
                              </label>
                              <p className="text-sm text-slate-500 mt-2">PNG, JPG, PDF, DOC up to 10MB</p>
                            </div>
                          </div>
                        </SectionRow>

                        <SectionRow title="Other details">
                          <div className="grid grid-cols-3 gap-4">
                            <Field label="Marital status">
                              <select name="maritalStatus" value={form.maritalStatus} onChange={handleChange} className={selectClass}>
                                <option value="">Select</option>
                                <option value="single">Single</option>
                                <option value="married">Married</option>
                                <option value="divorced">Divorced</option>
                                <option value="widowed">Widowed</option>
                              </select>
                            </Field>

                            <Field label="Employment status">
                              <select
                                name="employmentStatus"
                                value={form.employmentStatus}
                                onChange={handleChange}
                                className={selectClass}
                              >
                                <option value="">Select</option>
                                <option value="employed">Employed</option>
                                <option value="unemployed">Unemployed</option>
                                <option value="self-employed">Self-employed</option>
                                <option value="student">Student</option>
                              </select>
                            </Field>

                            <Field label="Education level">
                              <select
                                name="educationLevel"
                                value={form.educationLevel}
                                onChange={handleChange}
                                className={selectClass}
                              >
                                <option value="">Select</option>
                                <option value="primary">Primary</option>
                                <option value="secondary">Secondary</option>
                                <option value="tertiary">Tertiary</option>
                                <option value="postgraduate">Postgraduate</option>
                              </select>
                            </Field>
                          </div>

                          <div className="flex gap-8 mt-5 text-sm text-slate-700 font-medium">
                            <label className="flex items-center gap-2">
                              <input type="checkbox" name="cardPrints" checked={form.cardPrints} onChange={handleChange} className={checkboxClass} />
                              Card Prints
                            </label>
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                name="wristBandPrints"
                                checked={form.wristBandPrints}
                                onChange={handleChange}
                                className={checkboxClass}
                              />
                              Wrist Band Prints
                            </label>
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                name="labelPrints"
                                checked={form.labelPrints}
                                onChange={handleChange}
                                className={checkboxClass}
                              />
                              Label Prints
                            </label>
                          </div>
                        </SectionRow>
                      </div>
                    )}

                    {activeTab === 'contacts' && (
                      <div className="space-y-8">
                        <SectionRow title="Contacts">
                          <div className="grid grid-cols-2 gap-4">
                            <Field label="Mobile phone number">
                              <div className="relative">
                                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                  name="mobilePhoneNumber"
                                  value={form.mobilePhoneNumber}
                                  onChange={handleChange}
                                  className={`${inputClass} pl-10`}
                                />
                              </div>
                            </Field>

                            <Field label="Primary email">
                              <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                  name="primaryEmail"
                                  value={form.primaryEmail}
                                  onChange={handleChange}
                                  className={`${inputClass} pl-10`}
                                />
                              </div>
                            </Field>
                          </div>
                        </SectionRow>

                        <SectionRow title="Addresses">
                          <div className="grid grid-cols-3 gap-4">
                            <Field label="Country">
                              <input name="country" value={form.country} onChange={handleChange} className={inputClass} />
                            </Field>
                            <Field label="City">
                              <input name="city" value={form.city} onChange={handleChange} className={inputClass} />
                            </Field>
                            <Field label="Postal code">
                              <input name="postalCode" value={form.postalCode} onChange={handleChange} className={inputClass} />
                            </Field>
                          </div>

                          <div className="grid grid-cols-2 gap-4 mt-4">
                            <Field label="Address line">
                              <input name="addressLine" value={form.addressLine} onChange={handleChange} className={inputClass} />
                            </Field>
                            <Field label="Residence">
                              <input name="residence" value={form.residence} onChange={handleChange} className={inputClass} />
                            </Field>
                          </div>
                        </SectionRow>

                        <SectionRow title="Language">
                          <div className="grid grid-cols-2 gap-4">
                            <Field label="Languages spoken">
                              <input
                                name="languagesSpoken"
                                value={form.languagesSpoken}
                                onChange={handleChange}
                                className={inputClass}
                              />
                            </Field>
                            <Field label="Preferred language">
                              <select
                                name="preferredLanguage"
                                value={form.preferredLanguage}
                                onChange={handleChange}
                                className={selectClass}
                              >
                                <option value="English">English</option>
                                <option value="Shona">Shona</option>
                                <option value="Ndebele">Ndebele</option>
                              </select>
                            </Field>
                          </div>
                        </SectionRow>
                      </div>
                    )}

                    {activeTab === 'employer' && (
                      <SectionRow title="Employer">
                        <div className="grid grid-cols-2 gap-4">
                          <Field label="Employer">
                            <input name="employer" value={form.employer} onChange={handleChange} className={inputClass} />
                          </Field>
                        </div>
                      </SectionRow>
                    )}

                    {activeTab === 'kin' && (
                      <SectionRow title="Next of Kin">
                        <div className="grid grid-cols-4 gap-4">
                          <Field label="Title">
                            <select name="kinTitle" value={form.kinTitle} onChange={handleChange} className={selectClass}>
                              <option value="">Select</option>
                              <option value="Mr">Mr.</option>
                              <option value="Mrs">Mrs.</option>
                              <option value="Ms">Ms.</option>
                              <option value="Dr">Dr.</option>
                            </select>
                          </Field>

                          <Field label="First name">
                            <input name="kinFirstName" value={form.kinFirstName} onChange={handleChange} className={inputClass} />
                          </Field>

                          <Field label="Family name">
                            <input name="kinFamilyName" value={form.kinFamilyName} onChange={handleChange} className={inputClass} />
                          </Field>

                          <Field label="Relation">
                            <select name="kinRelation" value={form.kinRelation} onChange={handleChange} className={selectClass}>
                              <option value="">Select</option>
                              <option value="spouse">Spouse</option>
                              <option value="parent">Parent</option>
                              <option value="child">Child</option>
                              <option value="sibling">Sibling</option>
                              <option value="other">Other</option>
                            </select>
                          </Field>
                        </div>

                        <div className="grid grid-cols-3 gap-4 mt-4">
                          <Field label="Gender">
                            <select name="kinGender" value={form.kinGender} onChange={handleChange} className={selectClass}>
                              <option value="">Select</option>
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                              <option value="other">Other</option>
                            </select>
                          </Field>

                          <Field label="Birth date">
                            <input type="date" name="kinBirthDate" value={form.kinBirthDate} onChange={handleChange} className={inputClass} />
                          </Field>

                          <Field label="Mobile phone number">
                            <input
                              name="kinMobilePhoneNumber"
                              value={form.kinMobilePhoneNumber}
                              onChange={handleChange}
                              className={inputClass}
                            />
                          </Field>
                        </div>
                      </SectionRow>
                    )}

                    {activeTab === 'telemedicine' && (
                      <SectionRow title="Telemedicine">
                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                          <label className="flex items-center gap-3 text-sm font-medium text-slate-700">
                            <input
                              type="checkbox"
                              name="telemedicineEnabled"
                              checked={form.telemedicineEnabled}
                              onChange={handleChange}
                              className={checkboxClass}
                            />
                            Enable Telemedicine
                          </label>
                        </div>
                      </SectionRow>
                    )}

                    <div className="mt-12 flex justify-end gap-3">
                      {selectedPatient?.id && (
                        <button
                          type="button"
                          onClick={handleDeletePatient}
                          className="h-11 px-6 rounded-xl bg-red-50 text-red-600 border border-red-200 text-sm font-semibold hover:bg-red-100 transition inline-flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(e) => handleSubmit(e, false)}
                        className="h-11 px-6 rounded-xl bg-slate-700 text-white text-sm font-semibold hover:bg-slate-800 transition"
                      >
                        Save as Draft
                      </button>

                      <button
                        type="submit"
                        className="h-11 px-6 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition"
                      >
                        {selectedPatient ? 'Update Patient' : 'Save & Activate'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <button
                type="button"
                onClick={() => {
                  setSelectedPatient(null);
                  setForm(initialFormState);
                  setActiveTab('personal');
                  setMode('edit');
                }}
                className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl bg-violet-600 text-white text-lg font-semibold shadow-lg hover:bg-violet-700 transition"
              >
                <Plus className="w-5 h-5" />
                Create new patient
              </button>
            </div>
          )}

          <button
            type="button"
            className="fixed bottom-7 right-7 w-14 h-14 rounded-full bg-violet-600 text-white shadow-lg flex items-center justify-center hover:bg-violet-700 transition"
          >
            <Menu className="w-6 h-6" />
          </button>
        </section>
      </section>
    </div>
  );
};

function SummaryItem({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-2 text-sm font-medium text-slate-800">{value}</p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      {children}
    </div>
  );
}

function SectionRow({ title, children }) {
  return (
    <div className="grid grid-cols-[200px_1fr] gap-6">
      <div className={sectionTitleClass}>{title}</div>
      <div className="border-l border-slate-200 pl-6">{children}</div>
    </div>
  );
}

export default Patients;